import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, EmptyState } from '@eptai/eptai-app-components';
import { reportReactBoundaryError } from '../../utils/reportFrontendDiagnostic';

// ============================================================
// Models
// ============================================================

export type AppErrorBoundaryVariant = 'page' | 'route' | 'inline';

export type AppErrorBoundaryProps = {
  children: ReactNode;
  variant?: AppErrorBoundaryVariant;
  resetKeys?: Array<string | number>;
  boundaryName?: string;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

// ============================================================
// Private methods
// ============================================================

function _resetKeysChanged(
  prevKeys: Array<string | number> | undefined,
  nextKeys: Array<string | number> | undefined
): boolean {
  if (!prevKeys || !nextKeys) {
    return false;
  }
  if (prevKeys.length !== nextKeys.length) {
    return true;
  }
  return nextKeys.some((key, index) => prevKeys[index] !== key);
}

/** Uses location assign so this works outside BrowserRouter (page-level boundary). */
function _GoHomeButton(): React.JSX.Element {
  return (
    <Button variant="link" onClick={() => { window.location.href = '/'; }}>
      Go home
    </Button>
  );
}

function _PageFallback({ onReload }: { onReload: () => void }): React.JSX.Element {
  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center w-100 p-4"
      style={{ minHeight: '100vh' }}
      role="alert"
    >
      <EmptyState
        variant="error"
        title="Something went wrong"
        message="An unexpected error occurred. You can reload the page or go back home."
        action={{ label: 'Reload', onClick: onReload }}
      />
      <div className="d-flex flex-wrap gap-2 justify-content-center align-items-center mt-2">
        <_GoHomeButton />
      </div>
    </div>
  );
}

function _RouteFallback({
  onReload,
  onReset,
}: {
  onReload: () => void;
  onReset: () => void;
}): React.JSX.Element {
  return (
    <div className="d-flex flex-column align-items-center p-5" role="alert">
      <EmptyState
        variant="error"
        title="Something went wrong"
        message="This page could not be displayed. Navigation is still available."
        action={{ label: 'Reload', onClick: onReload }}
      />
      <div className="d-flex flex-wrap gap-2 justify-content-center align-items-center mt-2">
        <Button variant="secondary" onClick={onReset}>
          Try again
        </Button>
        <_GoHomeButton />
      </div>
    </div>
  );
}

function _InlineFallback({ onReset }: { onReset: () => void }): React.JSX.Element {
  return (
    <div className="py-3" role="alert">
      <EmptyState
        variant="error"
        title="Something went wrong"
        message="This section could not be displayed due to an error."
        action={{ label: 'Try again', onClick: onReset }}
      />
    </div>
  );
}

// ============================================================
// Public methods
// ============================================================

/**
 * App-owned React error boundary with page / route / inline fallbacks.
 *
 * Example:
 * ```tsx
 * <AppErrorBoundary variant="route" resetKeys={[pathname]} boundaryName="route">
 *   {children}
 * </AppErrorBoundary>
 * ```
 */
class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportReactBoundaryError({
      boundary: this.props.boundaryName || this.props.variant || 'app',
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    if (
      this.state.hasError &&
      _resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  private _handleReload = (): void => {
    window.location.reload();
  };

  private _handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const variant = this.props.variant || 'page';
      if (variant === 'inline') {
        return <_InlineFallback onReset={this._handleReset} />;
      }
      if (variant === 'route') {
        return (
          <_RouteFallback onReload={this._handleReload} onReset={this._handleReset} />
        );
      }
      return <_PageFallback onReload={this._handleReload} />;
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
